import { useEffect, useState } from 'react';
import { Control, FormState } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import TextInput from '../../form/TextInput';
import ActiveClear from '../../form/ActiveClear';
import SelectMenu from '../../form/SelectMenu';
import CheckboxInput from '../../form/CheckboxInput';
import { TransportationType } from '../../../data/models/transportation-type.model';
import { useLoggedInProviderId } from '../../../hooks/useLoggedInProviderId';
import validationUtils from '../../../services/validationUtils';

interface TransportationInfoFormProps {
  control: Control;
  formState: FormState<any>;
  requiredFieldMessage: string;
  transportationTypes: TransportationType[];
  hasRequiredDocuments: boolean;
  hasValidAutoInsurance: boolean;
  isCTTChecked: boolean;
  isInternalUser: boolean;
  isNewProvider: boolean;
}

export default function TransportationInfoForm({
  control,
  formState,
  requiredFieldMessage,
  transportationTypes,
  hasRequiredDocuments = true,
  hasValidAutoInsurance = true,
  isCTTChecked = true,
  isInternalUser,
  isNewProvider,
}: TransportationInfoFormProps) {
  const { t } = useTranslation('common');
  const providerId = useLoggedInProviderId();
  const [disabledMessage, setDisabledMessage] = useState('');

  const internalCheckboxes = [
    {
      label: t('provider.hasUnlimitedTrips'),
      name: 'HasUnlimitedTrips',
    },
  ];

  const providerCheckboxes = [
    {
      label: t('provider.hasUnlimitedTrips'),
      name: 'HasUnlimitedTrips',
    },
  ];

  useEffect(() => {
    let msg = '';
    if (!isCTTChecked) {
      if (!hasRequiredDocuments) {
        msg += t('common.missingDocuments');
      }
      if (!hasValidAutoInsurance) {
        if (msg.length) {
          msg += ' / ';
        }
        msg += t('common.invalidAutoInsurance');
      }
    }
    setDisabledMessage(msg);
  }, [
    providerId,
    isCTTChecked,
    hasRequiredDocuments,
    hasValidAutoInsurance,
    t,
  ]);

  return (
    <>
      <SelectMenu
        label={t('provider.type')}
        name="TransportationTypeID"
        values={transportationTypes}
        valueAttr="transportationTypeID"
        labelAttr="transportationTypeName"
        required={true}
        control={control}
        errors={formState.errors}
        rules={{ required: requiredFieldMessage }}
      />
      <TextInput
        label={t('provider.npiNumber')}
        name="NPINumber"
        required={false}
        control={control}
        errors={formState.errors}
        inputProps={{ maxLength: 10 }}
        rules={{
          pattern: validationUtils.accountNumberValidation(
            t,
            'common.invalidNPINumber',
          ),
        }}
      />
      {isInternalUser &&
        internalCheckboxes.map((checkbox) => (
          <CheckboxInput
            key={checkbox.name}
            label={checkbox.label}
            name={checkbox.name}
            required={false}
            control={control}
          />
        ))}
      {!isInternalUser &&
        providerCheckboxes.map((checkbox) => (
          <CheckboxInput
            key={checkbox.name}
            label={checkbox.label}
            name={checkbox.name}
            required={false}
            control={control}
          />
        ))}
      <ActiveClear
        control={control}
        hideReadyClearToTransport={false}
        disabledReadyClearToTransport={!!disabledMessage || isCTTChecked}
        disableClearToTransport={!!disabledMessage && !isCTTChecked}
        hideClearToTransport={!!providerId}
        disabledMessage={disabledMessage ? disabledMessage : undefined}
      />
      {isInternalUser && (
        <CheckboxInput
          label={t('provider.isInternal')}
          name="IsInternal"
          required={false}
          control={control}
        />
      )}
    </>
  );
}
